import { type QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  type GetUnreadCountResponse,
  getUnreadCountOptions,
  getUnreadCountQueryKey,
  type ListNotificationsResponse,
  listNotificationsOptions,
  listNotificationsQueryKey,
  markAllReadMutation,
  markNotificationReadMutation,
  type NotificationResponse,
} from "@/api";

/** The dropdown shows the most recent page; the full page (MHS-840) will page through the rest. */
export const PANEL_PAGE_SIZE = 20;
const PANEL_QUERY = { query: { page: 1, page_size: PANEL_PAGE_SIZE } };

export const panelListOptions = () => listNotificationsOptions(PANEL_QUERY);
export const panelListKey = () => listNotificationsQueryKey(PANEL_QUERY);
export const unreadCountOptions = () => getUnreadCountOptions();
export const unreadCountKey = () => getUnreadCountQueryKey();

type List = ListNotificationsResponse;

/** Pure cache updates (unit-tested in notificationCache.test.ts). */
export function prependNotification(list: List | undefined, n: NotificationResponse): List | undefined {
  if (!list || list.data.some((x) => x.uuid === n.uuid)) return list;
  return {
    ...list,
    data: [n, ...list.data].slice(0, PANEL_PAGE_SIZE),
    meta: { ...list.meta, total: list.meta.total + 1 },
  };
}

export function markReadInList(list: List | undefined, uuid: string | null, readAt: string): List | undefined {
  if (!list) return list;
  return {
    ...list,
    data: list.data.map((n) => (!n.readAt && (uuid === null || n.uuid === uuid) ? { ...n, readAt } : n)),
  };
}

function setCount(queryClient: QueryClient, update: (count: number) => number) {
  queryClient.setQueryData<GetUnreadCountResponse>(unreadCountKey(), (prev) =>
    prev ? { ...prev, data: { count: Math.max(0, update(prev.data.count)) } } : prev,
  );
}

/**
 * Apply a live update to a cached query, unless a fetch is in flight: its
 * response may predate the event and would overwrite it, so refetch instead.
 */
function applyLive(queryClient: QueryClient, queryKey: readonly unknown[], apply: () => void) {
  if (queryClient.isFetching({ queryKey }) > 0) void queryClient.invalidateQueries({ queryKey });
  else apply();
}

/** Called by the user event stream when a notification arrives. */
export function receiveNotification(queryClient: QueryClient, n: NotificationResponse) {
  const list = queryClient.getQueryData<List>(panelListKey());
  if (list?.data.some((x) => x.uuid === n.uuid)) return;
  applyLive(queryClient, panelListKey(), () =>
    queryClient.setQueryData<List>(panelListKey(), (prev) => prependNotification(prev, n)),
  );
  if (!n.readAt) applyLive(queryClient, unreadCountKey(), () => setCount(queryClient, (c) => c + 1));
}

export function receiveUnreadCount(queryClient: QueryClient, count: number) {
  void queryClient.cancelQueries({ queryKey: unreadCountKey() });
  queryClient.setQueryData<GetUnreadCountResponse>(unreadCountKey(), (prev) => ({ ...prev, data: { count } }));
}

/** Refetch both after a reconnect (events may have been missed while offline). */
export function refreshNotifications(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: panelListKey() });
  void queryClient.invalidateQueries({ queryKey: unreadCountKey() });
}

/** Mark one / all read, optimistically; a failure refetches the truth. */
export function useNotificationActions() {
  const queryClient = useQueryClient();
  const markOne = useMutation({ ...markNotificationReadMutation(), onError: () => refreshNotifications(queryClient) });
  const markAll = useMutation({ ...markAllReadMutation(), onError: () => refreshNotifications(queryClient) });

  const markRead = (n: NotificationResponse) => {
    if (n.readAt) return;
    queryClient.setQueryData<List>(panelListKey(), (prev) => markReadInList(prev, n.uuid, new Date().toISOString()));
    setCount(queryClient, (c) => c - 1);
    markOne.mutate({ path: { notification_uuid: n.uuid } });
  };

  const markAllRead = () => {
    queryClient.setQueryData<List>(panelListKey(), (prev) => markReadInList(prev, null, new Date().toISOString()));
    setCount(queryClient, () => 0);
    markAll.mutate({});
  };

  return { markRead, markAllRead, markingAll: markAll.isPending };
}
